// In-app feedback collection for Chipless. Writes player-submitted feedback to
// RTDB under `feedback/{pushId}` so it can be reviewed alongside analytics.
// Best-effort and consent-free: we attach the anonymous auth uid (already used
// for room membership) plus an optional email and lightweight context.
import { database } from '../firebase/firebaseConfig';
import { ref, push, set } from 'firebase/database';
import { ensureAuth } from './auth';
import { captureFeedbackSubmitted } from './posthogAnalytics';

const MAX_MESSAGE_LENGTH = 2000;
const MAX_EMAIL_LENGTH = 254;
const VALID_RATINGS = ['up', 'down'];

/**
 * Submit a piece of feedback. Requires a message, a rating, or both — a
 * one-tap thumbs rating (end-game card) is a valid submission on its own.
 * @param {Object} params
 * @param {string} [params.message] - Free-text feedback.
 * @param {'up'|'down'} [params.rating] - Binary thumbs rating.
 * @param {string} [params.email] - Optional email for follow-up.
 * @param {Object} [params.context] - Optional context, e.g. { source }.
 * @returns {Promise<void>}
 */
export async function submitFeedback({ message = '', rating = null, email = '', context = {} } = {}) {
  const trimmedMessage = (message || '').trim();
  const normalizedRating = VALID_RATINGS.includes(rating) ? rating : null;
  if (!trimmedMessage && !normalizedRating) {
    throw new Error('Please write a message before sending.');
  }

  const trimmedEmail = (email || '').trim();

  // Anonymous uid — same identity used for room membership. Don't block the
  // submission on auth; fall through with userId:null if it fails.
  let userId = null;
  try {
    userId = await ensureAuth();
  } catch (error) {
    console.warn('📝 Feedback auth lookup failed, submitting anonymously:', error);
  }

  const entry = {
    message: trimmedMessage ? trimmedMessage.slice(0, MAX_MESSAGE_LENGTH) : null,
    rating: normalizedRating,
    email: trimmedEmail ? trimmedEmail.slice(0, MAX_EMAIL_LENGTH) : null,
    userId,
    source: context.source || 'unknown',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    createdAt: Date.now(),
  };

  try {
    const feedbackRef = push(ref(database, 'feedback'));
    await set(feedbackRef, entry);
    console.log('📝 Feedback submitted');
  } catch (error) {
    console.error('📝 Failed to submit feedback:', error);
    throw error;
  }

  // Mirror into PostHog after the RTDB write succeeds. RTDB stays the
  // canonical store; this only makes feedback readable alongside analytics.
  captureFeedbackSubmitted({
    source: entry.source,
    rating: entry.rating,
    message: entry.message,
    email: entry.email,
  });
}

export default { submitFeedback };
